import { Transform } from './types';
import { jqPath, valAt } from './jqPaths';

// ── Per-field transforms ──
//
// Each transform emits a *canonical* jq form so the parser can recognize it on
// round-trip. `applyTransforms` wraps a base path expression; all forms except
// `coalesce` are pipe stages operating on `.`. `coalesce` wraps the base path
// itself (`(BASE // .fallback)`) because the fallback is a path from the root,
// not from the piped value. Every form deliberately avoids `as $var`, `def`, and
// pipe-assignment so it never trips hasStructuralComplexity.

// Strict JSON-number grammar. Number() alone accepts tokens jq rejects ("0x1F", "Infinity"),
// which would make the emitted filter fail jq compilation.
const JSON_NUMBER_RE = /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?$/;

/** Emits a jq literal for a user-entered scalar: JSON numbers/booleans/null stay bare, everything else is quoted. */
function jqLiteral(raw: string): string {
  const s = (raw ?? '').trim();
  if (s === 'true' || s === 'false' || s === 'null') return s;
  if (JSON_NUMBER_RE.test(s)) return s;
  return JSON.stringify(raw ?? '');
}

/** True when jqLiteral emits `raw` unquoted — so a string like "123" or "false" would
 *  change type on re-save. Parsers keep such strings in Code mode. */
export function emitsUnquoted(raw: string): boolean {
  return !jqLiteral(raw).startsWith('"');
}

/** The typed JS value that the literal emitted by jqLiteral(raw) evaluates to — keeps previews type-accurate. */
function jqLiteralValue(raw: string): any {
  const s = (raw ?? '').trim();
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s === 'null') return null;
  if (JSON_NUMBER_RE.test(s)) return Number(s);
  return raw ?? '';
}

/** Parses a jq literal token back to the raw string the user entered (inverse of jqLiteral). */
export function parseJqLiteral(raw: string): string {
  const s = raw.trim();
  if (s.startsWith('"')) {
    try {
      return JSON.parse(s);
    } catch {
      return s.replace(/^"|"$/g, '');
    }
  }
  return s;
}

/** Normalizes a user-typed source path (".cached.lat", " cached.lat ") to the internal dotted form. */
function normalizePathArg(arg: string): string {
  return (arg ?? '').trim().replace(/^\.+/, '');
}

/** A divide argument is usable only when it parses to a finite, non-zero JSON number. */
function parseDivisor(arg?: string): number | null {
  const s = (arg ?? '').trim();
  if (!JSON_NUMBER_RE.test(s) && isNaN(Number(s))) return null;
  const d = Number(s);
  return isFinite(d) && d !== 0 ? d : null;
}

/** Emits the value-map object literal `{"from": to, ...}` for value_map transforms. */
function valueMapObject(pairs: { from: string; to: string }[]): string {
  const entries = pairs
    .filter((p) => p.from !== '')
    .map((p) => `${JSON.stringify(p.from)}: ${jqLiteral(p.to)}`);
  return `{${entries.join(', ')}}`;
}

/**
 * Emits a single pipe-stage for a transform (all kinds except coalesce, which wraps the base).
 * Stages coerce first (tonumber/tostring) so real-world payloads that send numbers as strings
 * (or vice versa) don't hard-error in jq — mirroring the JS preview's Number()/String() coercions.
 * Returns null for stages that cannot emit (unusable divide arg); applyTransformsJs skips the same ones.
 */
function transformStage(t: Transform): string | null {
  switch (t.kind) {
    case 'tonumber':
      return 'tonumber';
    case 'tostring':
      return 'tostring';
    case 'epoch_iso_s':
      return '(tonumber | todate)';
    case 'epoch_iso_ms':
      return '(tonumber / 1000 | floor | todate)';
    case 'divide': {
      const d = parseDivisor(t.arg);
      if (d === null) return null;
      // Keep the user's spelling when it's already a valid jq number ("1e6");
      // normalize only tokens jq would reject ("0x1F" → "31").
      const raw = (t.arg ?? '').trim();
      return `(tonumber / ${JSON_NUMBER_RE.test(raw) ? raw : d})`;
    }
    case 'split':
      return `(tostring | split(${JSON.stringify(t.arg ?? '')}))`;
    case 'default':
      return `(. // ${jqLiteral(t.arg ?? '')})`;
    // [tostring] index: numeric source values would error on a plain [.] object index.
    case 'value_map':
      return `(${valueMapObject(t.pairs ?? [])}[tostring] // .)`;
    default:
      return null;
  }
}

/**
 * Canonical jq for a field with NO mapped source path, only transforms (e.g. a hardcoded
 * event_type). A single `default` collapses to the plain literal — byte-identical to the
 * legacy literal-input output, so old saved configs and new ones converge. Anything else
 * pipes from an explicit `null` base, which the parser recognizes on round-trip.
 */
export function unmappedFieldExpr(transforms: Transform[]): string {
  if (transforms.length === 1 && transforms[0].kind === 'default') {
    return jqLiteral(transforms[0].arg ?? '');
  }
  return applyTransforms('null', transforms);
}

/** Wraps a base jq path expression with the given transforms, producing canonical jq. */
export function applyTransforms(
  baseExpr: string,
  transforms?: Transform[],
): string {
  if (!transforms || transforms.length === 0) return baseExpr;

  let expr = baseExpr;
  // Coalesce wraps the base path (fallbacks are root paths, not piped values); chains in order.
  const fallbacks = transforms
    .filter((t) => t.kind === 'coalesce' && normalizePathArg(t.arg ?? ''))
    .map((t) => jqPath(normalizePathArg(t.arg!).split('.')));
  if (fallbacks.length) expr = `(${expr} // ${fallbacks.join(' // ')})`;

  const stages = transforms
    .filter((t) => t.kind !== 'coalesce')
    .map(transformStage)
    .filter(Boolean) as string[];
  if (stages.length) expr = `(${expr} | ${stages.join(' | ')})`;

  return expr;
}

/** jq's `//` falls back on null AND false — mirror that exactly in previews. */
function jqAbsent(v: any): boolean {
  return v === undefined || v === null || v === false;
}

/**
 * jq `tonumber` semantics: numbers pass through, numeric strings parse, everything
 * else errors — modeled as NaN so the type validator flags it (JS `Number(null)`
 * would silently give 0 where jq errors).
 */
function jqToNumber(v: any): number {
  if (typeof v === 'number') return v;
  // Only strict JSON-number strings: Number() alone accepts tokens jq rejects
  // ("0x1F", " 5 "), which would make the preview pass where the filter errors.
  if (typeof v === 'string' && JSON_NUMBER_RE.test(v)) return Number(v);
  return NaN;
}

/** jq `tostring` semantics: strings pass through, everything else becomes its JSON text
 *  (JS `String({})` would give "[object Object]" where jq produces `{"a":1}`). */
function jqToString(v: any): string {
  return typeof v === 'string' ? v : (JSON.stringify(v) ?? 'null');
}

/** Applies transforms to a concrete JS value for the live preview (mirrors transformStage semantics). */
export function applyTransformsJs(
  value: any,
  transforms: Transform[] | undefined,
  rootInput: any,
): any {
  if (!transforms || transforms.length === 0) return value;

  let v = value;
  // Coalesce chain first, in pill order — matches the emitted `(BASE // .f1 // .f2)`.
  for (const t of transforms) {
    if (t.kind !== 'coalesce') continue;
    const fallbackPath = normalizePathArg(t.arg ?? '');
    if (fallbackPath && jqAbsent(v))
      v = valAt(rootInput, fallbackPath.split('.'));
  }

  for (const t of transforms) {
    switch (t.kind) {
      case 'tonumber':
        v = jqToNumber(v);
        break;
      case 'tostring':
        v = jqToString(v);
        break;
      case 'epoch_iso_s':
        v = epochToIso(jqToNumber(v));
        break;
      case 'epoch_iso_ms':
        v = epochToIso(Math.floor(jqToNumber(v) / 1000));
        break;
      case 'divide': {
        const d = parseDivisor(t.arg);
        if (d !== null) v = jqToNumber(v) / d;
        break;
      }
      case 'split':
        v = jqToString(v).split(t.arg ?? '');
        break;
      case 'default':
        if (jqAbsent(v)) v = jqLiteralValue(t.arg ?? '');
        break;
      case 'value_map': {
        // Mirror the emitted object literal exactly: empty `from` keys are dropped
        // by valueMapObject, and duplicate keys are last-wins in jq object literals.
        const key = jqToString(v);
        const hit = [...(t.pairs ?? [])]
          .reverse()
          .find((p) => p.from !== '' && p.from === key);
        // jq `map[tostring] // .` keeps the original when the mapped value is null/false.
        if (hit) {
          const mapped = jqLiteralValue(hit.to);
          if (!jqAbsent(mapped)) v = mapped;
        }
        break;
      }
      default:
        break;
    }
  }
  return v;
}

/** Converts an epoch-seconds value to an ISO8601 string (jq `todate` equivalent).
 *  Out-of-range epochs (Date only covers ±8.64e15 ms — e.g. nanosecond timestamps) would
 *  make toISOString throw mid-render; jq `todate` errors on them too, so model as NaN. */
function epochToIso(epochSeconds: number): any {
  if (!isFinite(epochSeconds) || Math.abs(epochSeconds) > 8.64e12) return NaN;
  return new Date(epochSeconds * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** Keeps coalesce pills ahead of pipe-stage pills (stable) so the visible pill order
 *  always matches execution order — applyTransforms hoists coalesce into the base
 *  expression, and the parser reads fallbacks back before pipe stages. */
export function normalizeTransforms(transforms: Transform[]): Transform[] {
  return [
    ...transforms.filter((t) => t.kind === 'coalesce'),
    ...transforms.filter((t) => t.kind !== 'coalesce'),
  ];
}

/** Preview text for a transformed value. NaN is the "jq would error here" marker —
 *  JSON.stringify would print it as "null", contradicting the NaN validation message. */
export function formatPreviewText(v: any): string | undefined {
  if (v === undefined) return undefined;
  return typeof v === 'number' && isNaN(v) ? 'NaN' : JSON.stringify(v);
}
